import React from 'react';

import {
  ParsedData,
  StepProps,
  formatDate,
  formatMoney,
  toCentsFromString,
  toMoney,
  track,
} from '@betterment/js-runtime';
import { Icon } from '@betterment/samba-react';
import { Button, CenteredWorkflowLayout, Flex, HR, ListRow, Text, WorkflowCard } from '@betterment/style-closet-react';
import { get as _get } from 'es-toolkit/compat';
import { Form, Formik, FormikHelpers } from 'formik';

import { WithdrawalValues } from './Controller';

import { useAccount } from '../Checking/components/AccountProvider';
import { Customer, TransferAvailabilityEstimate } from '../Checking/data';
import { applyApiErrorsToFormik } from '../Checking/form-helpers';
import { intoBettermentAccount } from '../Checking/models/account';
import { intoBankAccount } from '../Checking/models/external-account';
import { AccountOption } from '../Shared/components/AccountOption';
import FlashErrorMessage from '../Shared/components/FlashErrorMessage';
import Footer from '../Shared/components/Footer';

interface Props extends StepProps<WithdrawalValues> {
  availabilityEstimate: TransferAvailabilityEstimate;
  customer: Customer;
  onWithdrawal: (values: WithdrawalValues) => Promise<ParsedData<{}>>;
}

const WithdrawalFlowConfirm: React.FC<React.PropsWithChildren<Props>> = ({
  flowState,
  goToNextStep,
  goToPreviousStep,
  customer,
  availabilityEstimate,
  onWithdrawal,
}) => {
  const { account } = useAccount();

  if (customer.externalAccount == null) return null;

  const formattedAmount = formatMoney(toMoney(toCentsFromString(flowState.values.amount)));

  const estimatedDate = availabilityEstimate && formatDate(availabilityEstimate.estimatedDate, 'longOrdinalWithDay');

  const handleSubmit = async (values: WithdrawalValues, formik: FormikHelpers<WithdrawalValues>) => {
    let result;
    try {
      result = await onWithdrawal({ amount: flowState.values.amount, externalAccountId: values.externalAccountId });
    } finally {
      formik.setSubmitting(false);
    }

    const { errors } = result;
    if (errors) {
      const hasTransactionLimitError = _get(errors.messages, 'amountCents', []).join(', ').includes('must be between');
      if (hasTransactionLimitError) {
        track('ElementViewed', { name: 'TransactionLimitReached', flow: 'WithdrawFromChecking' });
      }
      applyApiErrorsToFormik(formik, errors);
    } else {
      goToNextStep();
    }
  };

  return (
    <CenteredWorkflowLayout title="Withdrawal" heading="Review your withdrawal" handleBackButton={goToPreviousStep}>
      <CenteredWorkflowLayout.Slot name="content">
        <Formik initialValues={flowState.values} onSubmit={handleSubmit}>
          <Form>
            <FlashErrorMessage name="base" />
            <FlashErrorMessage name="amount" />

            <WorkflowCard variant="compact" mb="8">
              <ListRow leadingTitle="From">
                <ListRow.Slot name="trailing">
                  <AccountOption account={intoBettermentAccount(account, customer)} showBalance={false} iconSize={8} />
                </ListRow.Slot>
              </ListRow>
              <HR variant="list-row" />
              <ListRow leadingTitle="To">
                <ListRow.Slot name="trailing">
                  <AccountOption account={intoBankAccount(customer.externalAccount)} showBalance={false} iconSize={8} />
                </ListRow.Slot>
              </ListRow>
              <HR variant="list-row" />
              <ListRow leadingTitle="Amount" trailingTitle={formattedAmount} />
            </WorkflowCard>
            <WorkflowCard mb="4">
              <Flex flexDirection="row" alignItems="flex-start">
                <Flex.Item mr="6">
                  <Icon name="Calendar" size="small" color="icon" />
                </Flex.Item>
                <Flex.Item>
                  <Text variant="copy" as="p" lineHeight="1" mb="2">
                    Estimated Availability
                  </Text>
                  <Text fontSize="1" color="bmtGrey50">
                    Your funds should be available by {estimatedDate}.
                  </Text>
                </Flex.Item>
              </Flex>
            </WorkflowCard>
            <Text fontSize="1" color="bmtGrey50">
              Clicking &quot;Submit withdrawal&quot; will authorize an ACH debit from your Checking account.
            </Text>
            <CenteredWorkflowLayout.Actions>
              <Button type="submit">Submit withdrawal</Button>
            </CenteredWorkflowLayout.Actions>
          </Form>
        </Formik>
      </CenteredWorkflowLayout.Slot>
      <CenteredWorkflowLayout.Slot name="footer">
        <Footer />
      </CenteredWorkflowLayout.Slot>
    </CenteredWorkflowLayout>
  );
};

export default WithdrawalFlowConfirm;
